import { type ReactElement } from "react";

import {
  ServerDataTableConfig,
  useServerDataTable,
} from "@/hooks/data-table/use-server-data-table";
import { Column, DataTable } from "./DataTable";
import { PaginationState } from "./DataPagination";

/**
 * Props de apresentação repassadas ao `DataTable`. Não inclui os campos que o
 * hook controla (data/isLoading/onFetchData/paginação/ref) — esses vêm do `config`.
 */
interface ServerDataTableProps<T, F, C> {
  config: ServerDataTableConfig<T, F, C>;
  columns: Column<T>[];
  storageKey?: string;
  className?: string;
  emptyMessage?: React.ReactNode | string;
  /** Mensagem exibida no lugar do `emptyMessage` quando a busca falha. */
  errorMessage?: React.ReactNode | string;
  searchPlaceholder?: string;
  defaultSortColumn?: string;
  defaultSortOrder?: "ASC" | "DESC";
  actionButton?: React.ReactNode;
  exportFilename?: string;
  onRowClick?: (row: T) => void;
  onRowDoubleClick?: (row: T) => void;
  getRowHref?: (row: T) => string | undefined;
  rowClassName?: (row: T) => string;
}

/**
 * Atalho de baixa cerimônia para listas paginadas server-side: junta
 * `useServerDataTable(config)` + `<DataTable>` num componente só. Use quando a lista
 * não precisa acessar `criteria`/`setFilter`/`invalidate` no corpo — caso contrário
 * use o hook cru (ex.: `PlanilhaComprasListComponent`, que tem card de resumo).
 *
 * @example
 * <ServerDataTable
 *   config={{ queryOptions: jobsQueries.list, fetchFilters: jobService.getJobFiltros, invalidateKey: jobsKeys.all }}
 *   columns={columns}
 *   storageKey="job-list-state"
 *   defaultSortColumn="job.id"
 *   defaultSortOrder="DESC"
 * />
 */
export function ServerDataTable<T, F, C>({
  config,
  errorMessage,
  emptyMessage,
  ...viewProps
}: ServerDataTableProps<T, F, C>): ReactElement {
  const { tableProps, isError, invalidate } = useServerDataTable(config);

  return (
    <DataTable<T, F, C>
      {...tableProps}
      {...viewProps}
      emptyMessage={emptyMessage}
      isError={isError}
      errorMessage={errorMessage}
      onRetry={invalidate}
    />
  );
}

// Re-export por conveniência ao tipar paginação em consumidores do wrapper.
export type { PaginationState };
